import styled from '@emotion/styled'
import { AutoStack, FlexSpinner, onFormatDate } from 'components'
import {
  Status1Icon,
  Status2Icon,
  Status3Icon,
} from 'components/CustomIcons/StatusIcons'
import { useDeleteBlobMutation } from 'components/DocumentGenerator/documentGenerator.generated'
import { DownloadLink } from 'components/DownloadLink'
import { EntityList, renderPersona } from 'components/EntityList'
import { ConfirmDeleteDialog } from 'dialogs/ConfirmDeleteDialog'
import { FormikActions } from 'formik'
import { ProvingDetailsParticipantDocumentForm } from 'forms/ProvingDetailsForm/documents/ProvingDetailsParticipantDocumentForm'
import { Box } from 'grid'
import { isInProvingRole, omitDeep } from 'helpers'
import { useDialogState } from 'hooks/useDialogState'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import { BookingStatus, Participant, ProvingUserRole } from 'model'
import {
  ConstrainMode,
  IColumn,
  Icon,
  IconButton,
  PanelType,
  PersonaSize,
  SelectionMode,
  Stack,
  TooltipHost,
} from 'office-ui-fabric-react'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'theme'
import { createFieldColumn } from 'views/helpers'
import {
  useDeleteProvingDocumentsMutation,
  useLoadProvingDocumentsQuery,
  useUpdateProvingDocumentsMutation,
} from 'views/ProvingDetailsView/hooks'
import { ParticipantListProps } from './index'

const StyledBox = styled(Box)`
  padding-top: 3px;
`

export const RouteHotelApprovalList: React.FunctionComponent<
  ParticipantListProps
> = ({ items, provingGround, proving, params, participants, ...props }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [panelState, showPanel, hidePanel] = useFormikPanelState()
  const [dialogState, showDialog, hideDialog] = useDialogState()
  const fieldColumn = React.useMemo(() => createFieldColumn(t), [t])
  const { loading, error, refetch } = useLoadProvingDocumentsQuery({
    variables: { id: proving!.id },
  })
  const [documentId, setDocumentId] = React.useState()
  const [documentUri, setDocumentUri] = React.useState()
  const [updateDocuments] = useUpdateProvingDocumentsMutation({
    onCompleted: (d) => {
      refetch()
    },
  })
  const [deleteDocuments] = useDeleteProvingDocumentsMutation({
    onCompleted: (d) => {
      refetch()
    },
  })
  const [deleteBlob] = useDeleteBlobMutation()
  const isProvingManager = isInProvingRole(
    proving!,
    ProvingUserRole.ProvingManager
  )
  const handleSubmit = async (values: any, actions: FormikActions<any>) => {
    actions.setSubmitting(true)
    try {
      let block: string

      switch (values.type) {
        case 'bookingConfirmationRouteHotel':
          block = 'fix5'
          break
        case 'bookingConfirmationFerry':
          block = 'fix4'
          break
        case 'bookingConfirmationHotel':
          block = 'fix1'
          break
        case 'bookingConfirmationRentalCar':
          block = 'fix2'
          break
        case 'bookingConfirmationFlight':
          block = 'fix3'
          break
        default:
          block = values.block ? values.block : 'default'
      }

      const newValues = omitDeep(values, ['name'])

      await updateDocuments({
        variables: {
          provingId: proving!._id,
          input: {
            block,
            ...newValues,
          },
        },
      })
      actions.setStatus(null)
    } catch (err) {
      actions.setStatus({ error: err })
    } finally {
      actions.setSubmitting(false)
    }
    hidePanel()
  }

  const trackHotelOutwardColumns: IColumn[] = []
  const trackHotelReturnColumns: IColumn[] = []

  const columns = React.useMemo(
    () => [
      fieldColumn('person', {
        onRender: (item: Participant, i, col) => {
          if (
            (!item.person &&
              item.unknownPerson &&
              item.unknownPerson.isUnknown === true &&
              item.unknownPerson.personType === 'user') ||
            (!item.person &&
              item.unknownPerson &&
              item.unknownPerson.isUnknown === true &&
              item.unknownPerson.personType === 'external')
          ) {
            return t('model.proving.participant.participantUnknown')
          } else if (
            (!item.person &&
              item.unknownPerson &&
              item.unknownPerson.isUnknown === false &&
              item.unknownPerson.personType === 'user') ||
            (!item.person &&
              item.unknownPerson &&
              item.unknownPerson.isUnknown === false &&
              item.unknownPerson.personType === 'external')
          ) {
            return `${item.unknownPerson.surName}, ${item.unknownPerson.firstName}`
          } else if (
            item.unknownPerson &&
            item.unknownPerson.isUnknown &&
            item.unknownPerson.personType === 'worker'
          ) {
            return item.percentage + '%, ' + item.unknownPerson.department
          } else if (
            item.unknownPerson &&
            item.unknownPerson.isUnknown &&
            item.unknownPerson.personType === 'supplier'
          ) {
            return item.unknownPerson.supplier
              ? `${item.unknownPerson.surName}, ${item.unknownPerson.firstName} - ${item.unknownPerson.supplier}`
              : '[Teilnehmer noch unbekannt]'
          } else if (
            item.unknownPerson &&
            !item.unknownPerson.isUnknown &&
            item.unknownPerson.personType === 'worker'
          ) {
            return (
              <Stack horizontal={true}>
                <Stack.Item>
                  {renderPersona({
                    size: PersonaSize.size24,
                  })(item, i, col)}
                </Stack.Item>
                <Stack.Item>
                  <StyledBox>{item.percentage}% </StyledBox>
                </Stack.Item>
              </Stack>
            )
          } else if (item.headOfProving && item.headOfProving.isHeadOfProving) {
            return (
              <Stack horizontal={true}>
                <Stack.Item>
                  {renderPersona({
                    size: PersonaSize.size24,
                  })(item, i, col)}
                </Stack.Item>
                <TooltipHost
                  content={
                    t('model.proving.participant.headOfProving') +
                    `: ${onFormatDate(
                      new Date(item.headOfProving.dateFrom)
                    )} - ${onFormatDate(new Date(item.headOfProving.dateTo))}`
                  }
                >
                  <Stack.Item>
                    <StyledBox>
                      <Icon iconName="PartyLeader" />
                    </StyledBox>
                  </Stack.Item>
                </TooltipHost>
              </Stack>
            )
          } else {
            return renderPersona({
              size: PersonaSize.size24,
            })(item, i, col)
          }
        },
        minWidth: 155,
        ungroupable: true,
      }),
      fieldColumn('department', {
        onRender: (item: Participant, i, col) => {
          return item.person && item.person.department
            ? item.person.department
            : item.unknownPerson && item.unknownPerson.department
            ? item.unknownPerson.department
            : ''
        },
        ungroupable: true,
      }),
      fieldColumn('orderId'),
    ],
    [
      trackHotelOutwardColumns,
      trackHotelReturnColumns,
      fieldColumn,
      renderPersona,
      onFormatDate,
      t,
    ]
  )

  for (let j = 0; j < 10; j++) {
    trackHotelOutwardColumns.push(
      fieldColumn(`trackHotels.outward.${j}`, {
        useFullFieldname: true,
        minWidth: 155,
        ungroupable: true,
        onRender: (item: Participant, i, col) => {
          if (
            item.trackHotels &&
            item.trackHotels.outward &&
            item.trackHotels.outward[j] &&
            item.trackHotels.outward[j].hotel
          ) {
            const trackHotel = item.trackHotels.outward[j]
            return (
              <Box style={{ whiteSpace: 'pre-line' }}>
                {trackHotel.bookingStatus === BookingStatus.Open ? (
                  <Status1Icon />
                ) : trackHotel.bookingStatus ===
                  BookingStatus.WorkInProgress ? (
                  <Status2Icon />
                ) : trackHotel.bookingStatus === BookingStatus.Finished ? (
                  <Status3Icon />
                ) : (
                  <></>
                )}{' '}
                {trackHotel.hotel!.title!}{' '}
                {onFormatDate(new Date(trackHotel!.dateFrom))} -{' '}
                {onFormatDate(new Date(trackHotel!.dateTo))}
              </Box>
            )
          } else {
            return <></>
          }
        },
      })
    )
    trackHotelReturnColumns.push(
      fieldColumn(`trackHotels.return.${j}`, {
        useFullFieldname: true,
        minWidth: 155,
        ungroupable: true,
        onRender: (item: Participant, i, col) => {
          if (
            item.trackHotels &&
            item.trackHotels.return &&
            item.trackHotels.return[j] &&
            item.trackHotels.return[j].hotel
          ) {
            const trackHotel = item.trackHotels.return[j]
            return (
              <Box style={{ whiteSpace: 'pre-line' }}>
                {trackHotel.bookingStatus === BookingStatus.Open ? (
                  <Status1Icon />
                ) : trackHotel.bookingStatus ===
                  BookingStatus.WorkInProgress ? (
                  <Status2Icon />
                ) : trackHotel.bookingStatus === BookingStatus.Finished ? (
                  <Status3Icon />
                ) : (
                  <></>
                )}{' '}
                {trackHotel.hotel!.title!}{' '}
                {onFormatDate(new Date(trackHotel!.dateFrom))} -{' '}
                {onFormatDate(new Date(trackHotel!.dateTo))}
              </Box>
            )
          } else {
            return <></>
          }
        },
      })
    )
  }

  if (!provingGround.hideHotels) {
    trackHotelOutwardColumns.forEach((thoc, i) => {
      if (
        proving &&
        proving.participants &&
        proving.participants.some(
          (p) =>
            p.trackHotels && p.trackHotels.outward && p.trackHotels.outward[i]
        )
      ) {
        columns.push(thoc)
      }
    })
    trackHotelReturnColumns.forEach((thrc, i) => {
      if (
        proving &&
        proving.participants &&
        proving.participants.some(
          (p) =>
            p.trackHotels && p.trackHotels.return && p.trackHotels.return[i]
        )
      ) {
        columns.push(thrc)
      }
    })
  }

  const documents = fieldColumn('documents', {
    onRender: (item: Participant, i, col) => {
      const docs: any[] = []
      // tslint:disable-next-line: no-unused-expression
      proving &&
        proving.documents &&
        proving.documents.forEach((d) => {
          if (
            d.participantId === item._id &&
            d.type === 'bookingConfirmationRouteHotel'
          ) {
            docs.push(
              <div>
                <IconButton
                  iconProps={{ iconName: 'Edit' }}
                  styles={{ root: { color: theme.palette.red, height: 20 } }}
                  disabled={!!isProvingManager || proving.status === 8}
                  onClick={() =>
                    showPanel({
                      documentId: d._id,
                      participantId: d.participantId,
                      type: d.type,
                      name: d.name,
                    })
                  }
                />
                <IconButton
                  iconProps={{ iconName: 'Delete' }}
                  styles={{ root: { color: theme.palette.red, height: 20 } }}
                  disabled={!!isProvingManager || proving.status === 8}
                  onClick={() => {
                    setDocumentId(d._id)
                    setDocumentUri(d.uri)
                    showDialog()
                  }}
                />
                <DownloadLink
                  uri={d.uri}
                  styles={{
                    root: {
                      alignSelf: 'start',
                    },
                  }}
                >
                  {d.name}
                </DownloadLink>
              </div>
            )
          }
        })
      return <AutoStack>{docs}</AutoStack>
    },
  })

  columns.push(documents)

  return (
    <FlexSpinner loading={loading} error={error}>
      <EntityList
        alternate={true}
        stickyHeader={true}
        selectionMode={SelectionMode.single}
        items={items}
        constrainMode={ConstrainMode.horizontalConstrained}
        columns={columns}
        {...props}
      />
      <FormikPanel
        {...panelState}
        type={PanelType.large}
        onSubmit={handleSubmit}
      >
        <ProvingDetailsParticipantDocumentForm
          proving={proving!}
          params={params}
          participants={participants}
        />
      </FormikPanel>
      <ConfirmDeleteDialog
        {...dialogState}
        itemCount={1}
        onConfirm={async () => {
          await deleteDocuments({
            variables: {
              provingId: proving!._id,
              documentIds: [documentId],
            },
          })
          await deleteBlob({
            variables: {
              uri: documentUri,
            },
          })
          hideDialog()
          refetch()
        }}
      />
    </FlexSpinner>
  )
}
